import { useCallback } from "react";
import { generateLinkToDownloadCSV, getResourceTo } from "react/utils/utils";

export function useGRID(nameSpace) {
  const getDateRangeFilters = useCallback((filter, customFormatter) => {
    const filters = {};
    const filterValue = filter.getValue();
    const { ini: startDate, fin: endDate } = filterValue;

    if (startDate) {
      filters.startDate = customFormatter ? customFormatter(startDate) : startDate;
    }

    if (endDate) {
      filters.endDate = customFormatter ? customFormatter(endDate) : endDate;
    }

    return filters;
  }, []);

  const setSortByClient = useCallback(() => {
    App.Forms[nameSpace].table.sortBy = 'client';
  }, []);

  const setCustomCustomizeColumns = useCallback(() => {
    App.Forms[nameSpace].actions.customizecolumns.setCallBacks({
      afterSaveList: () => {
        const router = App.Objects[nameSpace];

        router.list();
      }
    });
  }, [nameSpace]);

  const setCustomFilterBuilderFields = useCallback(({ fieldsToRemove }) => {
    if (!fieldsToRemove.length) return;

    const fieldsList = App.Forms[nameSpace]?.filterBuilder?.fieldList;
    const parsedFieldsList = Object.keys(fieldsList).filter(item => !fieldsToRemove.includes(item));

    App.Forms[nameSpace]?.filterBuilder?.setFieldsToDisplayOnFilterBuilder(parsedFieldsList);
    App.Forms[nameSpace]?.filterBuilder?.render();
  }, [nameSpace]);

  const setCustomOnClickRow = useCallback((callback) => {
    if (!callback) return;

    const grid = App.Forms[nameSpace].table;

    const onClickRow = (e, args) => {
      const item = args.grid.getData().data[args.row];

      if (!item.model.attributes) return;

      callback(item.model.attributes);
    };

    grid.onClick.unsubscribe(onClickRow);
    grid.onClick.subscribe(onClickRow);
  }, [nameSpace]);

  const setCustomColumnsFormatter = useCallback((customColumns) => {
    const tableColumns = App.Forms[nameSpace]?.table?.getColumns() || [];

    const updatedTableColums = tableColumns.map((columnItem) => {
      const customColumn = customColumns[columnItem.field];

      if (customColumn) {
        const { formatter } = customColumn;

        if (formatter) {
          return Object.assign(columnItem, {
            ...customColumn,
            formatter: (row, cell, value, column, dataContext) => formatter(value, dataContext.model.attributes)
          });
        }

        return Object.assign(columnItem, customColumn);
      }

      return columnItem;
    });

    updatedTableColums.length && App.Forms[nameSpace].table.setColumns(updatedTableColums);
  }, [nameSpace]);

  const setCustomSaveList = useCallback((name, fieldsToOverride, customFormatters) => {
    const {
      actions: { exportFormats },
      fields: formFields
    } = App.Forms[nameSpace];

    App.Forms[nameSpace].actions.exportFormats.setScreenName(nameSpace);
    App.Forms[nameSpace].actions.exportFormats.saveFieldList = () => {
      exportFormats.handlerToggleLoadingInBtn();

      const fields = exportFormats.getFieldsSelecteds();

      const finishExportEvent = () => {
        exportFormats.inProgress = false;
        exportFormats.handlerToggleLoadingInBtn();
        exportFormats.togglePopUp();
      };

      if(fields.length === 0) {
        finishExportEvent();
        return new Greenmile.Components
          .AlertModal("Monet.ExportFormats.error.noElementSelected")
          .show();
      }

      const date = new Date();
      const fileName = `${name}_${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}.csv`;

      const parsedFields = fields
        .map((field) => field.replace(/"/g, ''))
        .map((field) => fieldsToOverride[field] ?? field);

      let data = '';

      data += Object.values(parsedFields)
        .map((field) => {
          const fieldData = formFields[field];
          return fieldData?.translatedLabel || getResourceTo(fieldData?.label);
        })
        .join(',');

      data += '\n';

      const formattedData = App.Forms[nameSpace].dataProvider.data
        .map((item) => {
          const itemModel = item.model.attributes;

          return parsedFields
            .map((field) => {
              const value = itemModel[field];
              const customFormatter = customFormatters[field];

              switch (true) {
                case !!customFormatter:
                  return customFormatter(value, itemModel);
                case [null, undefined].includes(value):
                  return '';
                default:
                  return String(value).replace(/,/g, '.');
              }
            })
            .join(',');
        })
        .join('\n');

      data += formattedData;

      generateLinkToDownloadCSV({
        fileName,
        data,
        callback: () => finishExportEvent()
      });
    };
  }, [nameSpace]);

  const getParsedCollectionData = useCallback((data) => {
    const arrayOfData = data.map((item) => new App.Models[nameSpace](item));
    const parsedData = new App.Collections[`${nameSpace}s`]();

    arrayOfData.forEach((item) => {
      parsedData.add(item);
    });

    return parsedData;
  }, [nameSpace]);

  const getActualPage = useCallback(() => {
    const paginationFirst = App.Forms[nameSpace].pagination.first;

    return paginationFirst ? paginationFirst / 51 : 0;
  }, [nameSpace])

  const addDataToGrid = useCallback((data) => {
    const columns = App.Forms[nameSpace].table.getColumns();
    const parsedData = getParsedCollectionData(data);

    if (!data.length) {
      Monet.Grid.addDataToGrid(nameSpace, columns, parsedData, true);
      Monet.Grid.Utils.showGridNoResult(nameSpace);

      return;
    }

    Monet.Grid.Utils.hideGridNoResult(nameSpace);

    const actualData = App.Forms[nameSpace].dataProvider.data;
    const page = getActualPage();

    if (actualData.length === 0 || page === 0) {
      Monet.Grid.addDataToGrid(nameSpace, columns, parsedData, true);
    } else {
      Monet.Grid.appendDataToGridAfterPagination(nameSpace, columns, parsedData);
    }
  }, [nameSpace]);

  return {
    getDateRangeFilters,
    getActualPage,
    setSortByClient,
    setCustomCustomizeColumns,
    setCustomFilterBuilderFields,
    setCustomOnClickRow,
    setCustomColumnsFormatter,
    setCustomSaveList,
    addDataToGrid
  }
}
